/**
 * DataTable — antd Table 래퍼 (자동 높이 + 정렬 + 페이지네이션 기본값).
 *
 * 【역할】
 * - 프레임워크 표준 그리드. 부모 컨테이너 높이에 맞춰 pageSize / scroll.y 자동 계산.
 * - 컬럼에 sorter 미지정 시 기본 compare (숫자/날짜/문자열) 자동 주입.
 * - ellipsis/bordered/showSorterTooltip/sticky 등 일관된 기본값.
 *
 * 【주요 기능】
 * - autoHeight=true (기본): ResizeObserver 로 헤더/summary/페이지네이션 높이 차감 → pageSize 계산.
 * - autoHeight=false: body 내부 scroll.y 사용 (Tabs 안 Table 또는 대시보드 미니 Table 에 적합).
 * - pagination 기본: 20/50/100/200, total 표시 (LBL_TOTAL_RECORDS).
 * - enhanceColumns: children 재귀 확장 + dataIndex 있는 컬럼에 sorter 자동 주입.
 *
 * 【Props】
 * - columns: ColumnsType<T> — antd Table columns (sorter 자동 주입).
 * - autoHeight?: boolean — 부모 높이에 맞춰 pageSize 자동 계산 (기본 true).
 * - minBodyHeight?: number — autoHeight=false 일 때 body 최소 높이 (기본 160).
 * - rowHeightOverride?: number — size 별 기본 row 높이 덮어쓰기.
 * - (기타 TableProps<T> 전부 통과)
 *
 * 【사용 예시】
 * ```tsx
 * <DataTable<Row> columns={cols} dataSource={rows} rowKey="id" />
 * ```
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Table } from 'antd'
import type { TableProps } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import { useLabel } from '@/framework/useLabel'

export interface DataTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  /** antd Table columns — dataIndex 있는 컬럼에 기본 sorter 자동 주입됨. */
  columns: ColumnsType<T>
  /** 부모 컨테이너 높이에 맞춰 pageSize/scroll.y 자동 계산 (기본 true).
   *  autoHeight=true:
   *    - pagination 이 false 여도 강제로 페이징 ON (fit-to-height pageSize)
   *    - body 내부 스크롤 없음 (보이는 행 수 = pageSize)
   *  autoHeight=false: 기존 scroll.y 자동 동작 (Tabs 안 Table, Dashboard 미니 Table 등)
   */
  autoHeight?: boolean
  /** autoHeight=false 일 때 body 최소 높이 (기본 160). */
  minBodyHeight?: number
  /** size 별 기본값을 덮어쓸 때 (기본 small=39, middle=55, large=63). */
  rowHeightOverride?: number
}

const ROW_HEIGHT_BY_SIZE: Record<NonNullable<TableProps<unknown>['size']>, number> = {
  small: 39,
  middle: 55,
  large: 63
}

/**
 * dataIndex(string | string[]) 경로로 객체 값을 안전하게 추출.
 * - path 가 배열이면 reduce 로 단계별 접근 (중첩 객체 지원).
 * - 중간에 null/undefined 가 나타나면 즉시 undefined 반환.
 */
function getByPath(obj: unknown, path: unknown): unknown {
  if (path == null) return undefined
  if (Array.isArray(path)) {
    return path.reduce<unknown>(
      (acc, k) => (acc == null ? acc : (acc as Record<string, unknown>)[k as string]),
      obj
    )
  }
  if (obj == null) return undefined
  return (obj as Record<string, unknown>)[path as string]
}

/**
 * 기본 정렬 비교자 — sorter 미지정 컬럼에 자동 주입.
 * - null 우선순위: null < value (null 을 위로).
 * - 둘 다 number 면 숫자 비교, Date.parse 가능하면 날짜 비교, 그 외 localeCompare 문자열 비교.
 */
function defaultCompare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  const da = Date.parse(String(a))
  const db = Date.parse(String(b))
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db
  return String(a).localeCompare(String(b))
}

/**
 * columns 트리를 재귀 순회하며 기본값을 주입.
 * - children 이 있는 그룹 컬럼은 재귀 처리.
 * - ellipsis 미지정 시 { showTitle: true } (긴 텍스트 ... + 툴팁).
 * - dataIndex 가 있고 sorter 가 undefined 인 컬럼에 defaultCompare 자동 주입 (sorter=false 는 보존).
 */
function enhanceColumns<T>(columns: ColumnsType<T>): ColumnsType<T> {
  return columns.map((col) => {
    const grp = col as { children?: ColumnsType<T> }
    if ('children' in col && Array.isArray(grp.children)) {
      return { ...col, children: enhanceColumns(grp.children) } as typeof col
    }
    const c = col as ColumnType<T>
    const next: ColumnType<T> = {
      ellipsis: c.ellipsis ?? { showTitle: true },
      ...c
    }
    if (c.dataIndex != null && c.sorter === undefined) {
      next.sorter = (ra: T, rb: T) =>
        defaultCompare(getByPath(ra, c.dataIndex), getByPath(rb, c.dataIndex))
    }
    return next
  })
}

/**
 * DataTable — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function DataTable<T extends object>(props: DataTableProps<T>) {
  const {
    columns,
    autoHeight = true,
    minBodyHeight = 160,
    rowHeightOverride,
    scroll,
    pagination,
    size,
    bordered,
    showSorterTooltip,
    sticky,
    style,
    ...rest
  } = props
  const { t } = useLabel()

  const containerRef = useRef<HTMLDivElement>(null)
  const [availableH, setAvailableH] = useState<number | null>(null)

  const resolvedSize = size ?? 'small'
  const rowHeight = rowHeightOverride ?? ROW_HEIGHT_BY_SIZE[resolvedSize]

  useEffect(() => {
    if (!autoHeight) return
    const el = containerRef.current
    if (!el) return

    let raf = 0
    const measure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect()
        const headerEl = el.querySelector('.ant-table-thead') as HTMLElement | null
        const summaryEl = el.querySelector('.ant-table-summary') as HTMLElement | null
        const paginationEl = el.querySelector(
          '.ant-table-pagination, .ant-pagination'
        ) as HTMLElement | null
        const headerH = headerEl?.getBoundingClientRect().height ?? 40
        const summaryH = summaryEl?.getBoundingClientRect().height ?? 0
        const paginationH = paginationEl?.getBoundingClientRect().height ?? 0
        const paginationMarginV = paginationH > 0 ? 32 : 0
        // 가로 스크롤바(일부 브라우저 15~17px) + 테이블 bottom border + safety 여유
        const safetyBuffer = 20
        const available =
          rect.height - headerH - summaryH - paginationH - paginationMarginV - safetyBuffer
        setAvailableH(Math.max(minBodyHeight, Math.floor(available)))
      })
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    if (el.parentElement) ro.observe(el.parentElement)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [autoHeight, scroll, minBodyHeight])

  const enhanced = useMemo(() => enhanceColumns(columns), [columns])

  const autoPageSize = useMemo(() => {
    if (availableH == null) return null
    return Math.max(1, Math.floor(availableH / rowHeight))
  }, [availableH, rowHeight])

  const finalPagination = useMemo(() => {
    if (autoHeight) {
      const base = {
        showSizeChanger: true,
        showQuickJumper: false,
        showTotal: (total: number) =>
          t('LBL_TOTAL_RECORDS', 'Total {0}').replace('{0}', String(total)),
        pageSizeOptions: ['20', '50', '100', '200']
      }
      if (pagination === false) {
        return { ...base, pageSize: autoPageSize ?? 20 }
      }
      const userPageSize = (pagination as { pageSize?: number } | undefined)?.pageSize
      return {
        ...base,
        pageSize: userPageSize ?? autoPageSize ?? 20,
        ...(pagination ?? {})
      }
    }
    if (pagination === false) return false
    return {
      pageSize: 50,
      showSizeChanger: true,
      showQuickJumper: false,
      showTotal: (total: number) =>
        t('LBL_TOTAL_RECORDS', 'Total {0}').replace('{0}', String(total)),
      pageSizeOptions: ['20', '50', '100', '200'],
      ...pagination
    }
  }, [autoHeight, pagination, autoPageSize, t])

  const finalScroll = useMemo(() => {
    if (!autoHeight) {
      const next: Record<string, unknown> = {}
      if (scroll?.x != null) next.x = scroll.x
      if (availableH != null) next.y = availableH
      else if (scroll?.y != null) next.y = scroll.y
      return Object.keys(next).length ? (next as typeof scroll) : undefined
    }
    // autoHeight 모드: body 높이를 available 로 고정 → 데이터 수와 무관하게
    // pagination 이 컨테이너 하단에 일관된 위치로 표시되도록 한다.
    const next: Record<string, unknown> = {}
    if (scroll?.x != null) next.x = scroll.x
    if (availableH != null) next.y = availableH
    else if (scroll?.y != null) next.y = scroll.y
    return Object.keys(next).length ? (next as typeof scroll) : undefined
  }, [scroll, autoHeight, availableH])

  return (
    <div
      ref={containerRef}
      className="fw-datatable-wrap"
      style={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...style
      }}
    >
      <Table<T>
        size={resolvedSize}
        bordered={bordered ?? false}
        showSorterTooltip={showSorterTooltip ?? { target: 'sorter-icon' }}
        sticky={sticky ?? true}
        {...rest}
        columns={enhanced}
        scroll={finalScroll}
        pagination={finalPagination}
      />
    </div>
  )
}
